import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../hooks/useAuth';
import { ArrowLeft, Plus, Trash2, Mail, Phone, Star, FileText } from 'lucide-react';
import { format } from 'date-fns';

const TAG_STYLES = {
  'active':     'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  'pending':    'bg-amber-500/15 text-amber-400 border-amber-500/30',
  'slow':       'bg-red-500/15 text-red-400 border-red-500/30',
  'high-value': 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  'hot':        'bg-orange-500/15 text-orange-400 border-orange-500/30',
};

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({ name:'', email:'', phone:'', job_title:'', is_primary: false });
  const [savingContact, setSavingContact] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/clients/${id}`);
      setClient(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const saveContact = async (e) => {
    e.preventDefault();
    setSavingContact(true);
    try {
      await api.post('/contacts', { ...contactForm, client_id: id });
      setShowContactForm(false);
      setContactForm({ name:'', email:'', phone:'', job_title:'', is_primary: false });
      load();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setSavingContact(false); }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Remove this contact?')) return;
    await api.delete(`/contacts/${contactId}`);
    load();
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-96">
      <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!client) return <div className="p-6 text-slate-400">Client not found</div>;

  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6 transition-colors">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left: client info */}
        <div className="xl:col-span-2 space-y-6">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-white">{client.company_name}</h1>
                {client.industry && <p className="text-slate-400 text-sm mt-1">{client.industry}</p>}
                {client.website && (
                  <a href={client.website} target="_blank" rel="noreferrer" className="text-blue-400 text-sm hover:underline">{client.website}</a>
                )}
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                {(client.tags || []).map(tag => (
                  <span key={tag} className={`text-xs font-semibold px-2.5 py-1 rounded-full border capitalize ${TAG_STYLES[tag] || 'bg-slate-800 text-slate-400 border-slate-700'}`}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            {client.notes && (
              <div className="mt-4 pt-4 border-t border-slate-800 text-sm text-slate-400">
                {client.notes}
              </div>
            )}
          </div>

          {/* Contacts */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h2 className="font-semibold text-white">Contacts</h2>
              <button
                onClick={() => setShowContactForm(!showContactForm)}
                className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 font-medium"
              >
                <Plus size={13} /> Add Contact
              </button>
            </div>

            {showContactForm && (
              <form onSubmit={saveContact} className="px-6 py-4 border-b border-slate-800 space-y-3 bg-slate-800/30">
                <div className="grid grid-cols-2 gap-3">
                  <input required placeholder="Full Name *" value={contactForm.name} onChange={e => setContactForm(f => ({...f, name: e.target.value}))}
                    className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500" />
                  <input required type="email" placeholder="Email *" value={contactForm.email} onChange={e => setContactForm(f => ({...f, email: e.target.value}))}
                    className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500" />
                  <input placeholder="Phone" value={contactForm.phone} onChange={e => setContactForm(f => ({...f, phone: e.target.value}))}
                    className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500" />
                  <input placeholder="Job Title" value={contactForm.job_title} onChange={e => setContactForm(f => ({...f, job_title: e.target.value}))}
                    className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500" />
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
                  <input type="checkbox" checked={contactForm.is_primary} onChange={e => setContactForm(f => ({...f, is_primary: e.target.checked}))} className="accent-blue-600" />
                  Primary contact
                </label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setShowContactForm(false)} className="text-xs px-3 py-1.5 border border-slate-700 rounded-lg text-slate-400 hover:bg-slate-800">Cancel</button>
                  <button type="submit" disabled={savingContact} className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium">Save</button>
                </div>
              </form>
            )}

            <div className="divide-y divide-slate-800/50">
              {(client.contacts || []).length === 0 && !showContactForm && (
                <div className="px-6 py-8 text-center text-slate-500 text-sm">No contacts yet</div>
              )}
              {(client.contacts || []).map(c => (
                <div key={c.id} className="px-6 py-4 flex items-center gap-4">
                  <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-sm font-bold text-slate-400 flex-shrink-0">
                    {c.name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white">{c.name}</span>
                      {c.is_primary && <Star size={12} className="text-amber-400" fill="currentColor" />}
                    </div>
                    <div className="text-xs text-slate-500">{c.job_title}</div>
                  </div>
                  <div className="text-right space-y-1">
                    <div className="flex items-center gap-1 text-xs text-slate-400 justify-end">
                      <Mail size={11} /> {c.email}
                    </div>
                    {c.phone && <div className="flex items-center gap-1 text-xs text-slate-500 justify-end">
                      <Phone size={11} /> {c.phone}
                    </div>}
                  </div>
                  <button onClick={() => deleteContact(c.id)} className="text-slate-600 hover:text-red-400 p-1.5 transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: recent documents */}
        <div>
          <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h2 className="font-semibold text-white">Recent Documents</h2>
              <button
                onClick={() => navigate('/documents/new')}
                className="text-xs text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1"
              >
                <Plus size={12} /> Send
              </button>
            </div>
            <div className="divide-y divide-slate-800/50">
              {(client.recent_documents || []).length === 0 && (
                <div className="px-6 py-8 text-center text-slate-500 text-sm">No documents yet</div>
              )}
              {(client.recent_documents || []).map(doc => (
                <div
                  key={doc.id}
                  onClick={() => navigate(`/documents/${doc.id}`)}
                  className="px-6 py-4 cursor-pointer hover:bg-slate-800/30 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <FileText size={16} className="text-slate-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">{doc.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {doc.sent_at ? format(new Date(doc.sent_at), 'MMM d, yyyy') : 'Draft'}
                        {' · '}
                        <span className="capitalize">{doc.status?.replace(/_/g, ' ')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
